const INTERACTION_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'touchstart',
  'scroll',
] as const;

/**
 * Runs `task` (in an idle callback) once the page has finished loading and the
 * user first interacts with it — or after `fallbackMs`, whichever comes first.
 *
 * For low-priority background work (e.g. preloading code) that should neither
 * compete with the current page's own requests nor land inside automated
 * page-load measurements, which never interact with the page.
 *
 * @returns cleanup that cancels the task if it has not started yet
 */
export function runAfterFirstInteraction(
  task: () => void,
  fallbackMs = 10_000
): () => void {
  let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
  let started = false;

  const cleanup = () => {
    window.removeEventListener('load', armAfterLoad);
    INTERACTION_EVENTS.forEach((event) =>
      window.removeEventListener(event, start)
    );
    clearTimeout(fallbackTimer);
  };

  function start() {
    if (started) {
      return;
    }
    started = true;
    cleanup();
    const scheduleIdle =
      window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 0));
    scheduleIdle(() => task());
  }

  function armAfterLoad() {
    INTERACTION_EVENTS.forEach((event) =>
      window.addEventListener(event, start, { once: true, passive: true })
    );
    fallbackTimer = setTimeout(start, fallbackMs);
  }

  if (document.readyState === 'complete') {
    armAfterLoad();
  } else {
    window.addEventListener('load', armAfterLoad, { once: true });
  }

  return cleanup;
}
